/* The market clock, in New York time (the market's own clock).
   Ported from the current terminal (templates/index.html, marketSession / closedWindow):
   same holidays, same early closes, same session bounds. Update the two sets once a year. */

export const NY_TZ = 'America/New_York';

export const NYSE_HOLIDAYS = new Set([
  '2026-11-26', '2026-12-25', '2027-01-01', '2027-01-18', '2027-02-15', '2027-03-26',
  '2027-05-31', '2027-06-18', '2027-07-05', '2027-09-06', '2027-11-25', '2027-12-24',
]);
export const NYSE_EARLY_CLOSE = new Set(['2026-11-27', '2026-12-24', '2027-11-26']);

const nyFmt = new Intl.DateTimeFormat('en-US', {
  timeZone: NY_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', hourCycle: 'h23', weekday: 'short',
});

export interface NyParts { iso: string; mins: number; wd: string }

export function nyParts(d: Date): NyParts {
  const p: Record<string, string> = {};
  nyFmt.formatToParts(d).forEach(x => { p[x.type] = x.value; });
  return { iso: `${p.year}-${p.month}-${p.day}`, mins: (Number(p.hour) % 24) * 60 + Number(p.minute), wd: p.weekday ?? '' };
}

export const isTradingDay = (iso: string, wd: string): boolean => wd !== 'Sat' && wd !== 'Sun' && !NYSE_HOLIDAYS.has(iso);
export const closeMins = (iso: string): number => (NYSE_EARLY_CLOSE.has(iso) ? 13 * 60 : 16 * 60);

/** The instant a New York wall-clock time happens (DST-safe: corrects the first guess). */
export function nyInstant(iso: string, mins: number): number {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  let t = Date.UTC(y, m - 1, d, Math.floor(mins / 60), mins % 60) + 4 * 3600e3;
  for (let i = 0; i < 2; i++) {
    const p = nyParts(new Date(t));
    const dayShift = p.iso === iso ? 0 : p.iso < iso ? 1440 : -1440;
    t += (mins - p.mins + dayShift) * 60e3;
  }
  return t;
}

export interface NextOpen { at: number; wd: string }

export function nextOpen(now: Date): NextOpen | null {
  const today = nyParts(now);
  for (let i = 0; i < 12; i++) {
    const p = nyParts(new Date(nyInstant(today.iso, 12 * 60) + i * 86400e3));
    if (!isTradingDay(p.iso, p.wd)) continue;
    const at = nyInstant(p.iso, 9 * 60 + 30);
    if (at > now.getTime()) return { at, wd: p.wd };
  }
  return null;
}

export type SessionState = 'open' | 'pre' | 'after' | 'closed';

export interface Session {
  state: SessionState;
  label: 'Open' | 'Pre-market' | 'After hours' | 'Closed' | 'Holiday';
  /** open and pre-market: when the state ends */
  until?: number;
  verb?: 'closes' | 'opens';
  /** after hours and closed: the next opening bell */
  next?: NextOpen | null;
}

export function marketSession(now: Date = new Date()): Session {
  const p = nyParts(now), close = closeMins(p.iso);
  if (isTradingDay(p.iso, p.wd)) {
    if (p.mins >= 570 && p.mins < close) return { state: 'open', label: 'Open', until: nyInstant(p.iso, close), verb: 'closes' };
    if (p.mins >= 240 && p.mins < 570) return { state: 'pre', label: 'Pre-market', until: nyInstant(p.iso, 570), verb: 'opens' };
    if (p.mins >= close && p.mins < 20 * 60) return { state: 'after', label: 'After hours', next: nextOpen(now) };
  }
  return { state: 'closed', label: NYSE_HOLIDAYS.has(p.iso) ? 'Holiday' : 'Closed', next: nextOpen(now) };
}

/** Shown from the evening before a non-trading day until it ends; an ordinary weeknight keeps the desk. */
export function closedWindow(now: Date = new Date()): { session: Session; next: NextOpen; key: string } | null {
  const s = marketSession(now);
  if (s.state !== 'closed' || !s.next) return null;
  const today = nyParts(now);
  const tomorrow = nyParts(new Date(nyInstant(today.iso, 12 * 60) + 86400e3)).iso;
  const openIso = nyParts(new Date(s.next.at)).iso;
  if (isTradingDay(today.iso, today.wd) && (openIso === today.iso || openIso === tomorrow)) return null;
  return { session: s, next: s.next, key: openIso };
}

export function fmtSpan(ms: number): string {
  const m = Math.max(0, Math.round(ms / 60e3)), h = Math.floor(m / 60);
  return h ? `${h}h ${String(m % 60).padStart(2, '0')}m` : `${m}m`;
}

/** "1d 14h 05m", "3h 05m", "9m": the countdown to a reopening */
export function fmtLongSpan(ms: number): string {
  const m = Math.max(0, Math.round(ms / 60e3)), d = Math.floor(m / 1440), h = Math.floor((m % 1440) / 60);
  return (d ? `${d}d ` : '') + (d || h ? `${h}h ` : '') + String(m % 60).padStart(d || h ? 2 : 1, '0') + 'm';
}

export const WEEKDAY_LONG: Record<string, string> = { Mon: 'Monday', Tue: 'Tuesday', Wed: 'Wednesday', Thu: 'Thursday', Fri: 'Friday', Sat: 'Saturday', Sun: 'Sunday' };

/** Whether a closure's panel was dismissed (remembered per closure, keyed by the reopening day). */
export function closedDismissed(storageKey: string, closureKey: string): boolean {
  try { return localStorage.getItem(storageKey) === closureKey; } catch { return false; }
}
export function setClosedDismissed(storageKey: string, closureKey: string | null): void {
  try { if (closureKey) localStorage.setItem(storageKey, closureKey); else localStorage.removeItem(storageKey); } catch { /* not remembered */ }
}
